import type { DefaultOptionType } from 'antd/es/select'
import { sortBy } from 'lodash-es'
import type { ISelectDropdownProps } from '@jana-technology/lht-select-dropdown'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'

import { IContinuousRoute } from '$/types'
import RangeSlider from 'common/components/RangeSlider'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

import { ILocalFilter } from '..'
import { IFilterDetailsProps } from './types'

interface INetworkDropdownProps
  extends Omit<
    IFilterDetailsProps,
    'setIsLocalFilterStateChanged' | 'setCanFilterBeApplied'
  > {
  addRoute: () => void
  removeRoute: (routeId: number) => void
  selectRoute: (
    selections: Parameters<
      Exclude<
        ISelectDropdownProps<DefaultOptionType>['onChangeSelections'],
        undefined
      >
    >['0'],
    index: number
  ) => void
  updateRouteRange: (routeId: number, range: number[]) => void
  routeOptions: DefaultOptionType[]
  selectedClassLocations: DefaultOptionType[]
  classLocationOptions: DefaultOptionType[]
  riskLevelOptions: DefaultOptionType[]
  consequenceOptions: DefaultOptionType[]
  localFilter: ILocalFilter
}

interface IRouteSelectRowProps
  extends Pick<
    INetworkDropdownProps,
    'routeOptions' | 'selectRoute' | 'updateRouteRange' | 'removeRoute'
  > {
  routeIndex: number
  selectedRoute: IContinuousRoute
  testIndex: number
  segment?: IContinuousRoute
}

const RouteSelectRow = ({
  segment,
  routeOptions,
  selectRoute,
  selectedRoute,
  updateRouteRange,
  removeRoute,
  routeIndex,
  testIndex
}: IRouteSelectRowProps) => (
  <div className='py-2 flex gap-x-4' data-testid={`route ${testIndex}`}>
    <div className='w-64'>
      <SelectDropdownWrapper
        showSearch
        optionFilterProp='label'
        className='w-full'
        allowClear={false}
        options={sortBy(routeOptions, [
          (i) => (typeof i.label === 'string' ? i.label : '').toLowerCase(),
          (i) => (typeof i.label === 'string' ? i.label : '')
        ])}
        onChangeSelections={(selections) => selectRoute(selections, routeIndex)}
        value={{
          label: selectedRoute.name,
          value: selectedRoute.rowId
        }}
      />
    </div>
    <div className='flex flex-1 flex-col w-96'>
      <div className='flex gap-x-2'>
        <div className='flex-1'>
          <RangeSlider
            disabled={!selectedRoute.id || selectedRoute.id < 0}
            enableInput
            min={Math.min(
              segment?.fromMeasure ?? 0,
              selectedRoute.initialFromMeasure
            )}
            max={selectedRoute.initialToMeasure}
            value={[selectedRoute.fromMeasure, selectedRoute.toMeasure]}
            setRange={(range) => updateRouteRange(selectedRoute.rowId, range)}
          />
        </div>
        <button
          className='text-gray-700'
          onClick={() => removeRoute(selectedRoute.rowId)}
          data-testid='clear-first-segment'
        >
          <CancelIcon fontSize='small' />
        </button>
      </div>
    </div>
  </div>
)

export const NetworkDropdown = ({
  localFilter,
  routeOptions,
  selectRoute,
  updateRouteRange,
  addRoute,
  removeRoute,
  onHMCASelection,
  onClassLocationSelection,
  onRiskLevelSelection,
  selectedRoutes,
  selectedHmca,
  consequenceOptions,
  riskLevelOptions,
  selectedRiskLevels,
  classLocationOptions,
  selectedClassLocations
}: INetworkDropdownProps) => {
  // segement is needed for a static min value, if it's negative
  return (
    <div>
      <div className='m-6'>
        <div className='font-semibold'>Routes</div>
        <div className='mt-6 flex gap-x-4 font-medium'>
          <div className='w-64'>Route</div>
          <div>Range</div>
        </div>
        {selectedRoutes.map((route, index) => (
          <RouteSelectRow
            segment={localFilter.segments.find(
              (segment) => segment.rowId === route.rowId
            )}
            key={route.rowId}
            routeIndex={route.rowId}
            selectedRoute={route}
            routeOptions={routeOptions}
            selectRoute={selectRoute}
            updateRouteRange={updateRouteRange}
            removeRoute={removeRoute}
            testIndex={index}
          />
        ))}
        <button
          className='mt-4 flex items-center leading-none text-lighthouseBlue'
          onClick={addRoute}
        >
          <AddIcon />
          <span>Add Range</span>
        </button>
      </div>
      <hr className='h-2 bg-gray-200' />
      <div className='m-6 mb-12'>
        <div className='font-semibold'>Filter Within Selected Ranges</div>
        <div className='flex items-center justify-between border-b py-3'>
          <span className='font-medium'>Consequence Area</span>
          <SelectDropdownWrapper
            className='w-96'
            mode='multiple'
            options={consequenceOptions}
            value={selectedHmca}
            onChangeSelections={onHMCASelection}
            placeholder='All'
            maxTagTextLength={10}
            maxTagCount={2}
          />
        </div>
        <div className='flex items-center justify-between border-b py-3'>
          <span className='font-medium'>Class Location</span>
          <SelectDropdownWrapper
            className='w-96'
            mode='multiple'
            options={classLocationOptions}
            value={selectedClassLocations}
            onChangeSelections={onClassLocationSelection}
            placeholder='All'
            maxTagTextLength={10}
            maxTagCount={2}
          />
        </div>
        <div className='flex items-center justify-between py-3'>
          <span className='font-medium'>Risk Level</span>
          <SelectDropdownWrapper
            className='w-96'
            mode='multiple'
            options={riskLevelOptions}
            value={selectedRiskLevels}
            onChangeSelections={onRiskLevelSelection}
            placeholder='All'
            maxTagTextLength={10}
            maxTagCount={2}
          />
        </div>
      </div>
    </div>
  )
}
